/**
 * Copyright © Veeam Software Group GmbH.
 */

/**
 * Copyright © Veeam Software Group GmbH.
 */

const generateGuid = (): string => {
    // eslint-disable-next-line no-bitwise
    const s4 = () => (((1 + Math.random()) * 0x10000) | 0).toString(16).substring(1);

    return `${s4()}${s4()}-${s4()}-4${s4().substr(0, 3)}-${s4()}-${s4()}${s4()}${s4()}`.toLowerCase();
};

/**
 * Generates a truly random value.
 */
const generateRandomValue = (): string => `${window.crypto.getRandomValues(new Uint32Array(1))[0]}`;

const entityToChar: Record<string, string> = {
    /* eslint-disable @typescript-eslint/naming-convention */
    '&amp;': '&',
    '&gt;': '>',
    '&lt;': '<',
    '&quot;': '"',
    '&#39;': '\'',
};
const charToEntity = Object.fromEntries(Object.entries(entityToChar).map(([entity, char]: string[]) => [char, entity]));
const charToEntityRe = new RegExp(`(${Object.values(entityToChar).join('|')})`, 'g');
const entityToCharRe = new RegExp(`(${Object.keys(entityToChar).join('|')}|&#[0-9]{1,5};)`, 'g');
const htmlEncodeReplaceFn = (match: string, capture: string): string => charToEntity[capture];
const htmlDecodeReplaceFn = (match: string, capture: string): string => (capture in entityToChar)
    ? entityToChar[capture]
    : String.fromCharCode(parseInt(capture.substr(2), 10));

const htmlEncode = (value: string | null) => !value ? value : String(value).replace(charToEntityRe, htmlEncodeReplaceFn);

const htmlDecode = (value: string | null) => !value ? value : String(value).replace(entityToCharRe, htmlDecodeReplaceFn);

const isEncode = (str: string) => htmlDecode(str) !== str;

const getStrBetween = (s: string, prefix: string, suffix: string): string => {
    let i = s.indexOf(prefix);

    if (i < 0) {
        return '';
    }

    s = s.substring(i + prefix.length);

    if (suffix) {
        i = s.indexOf(suffix);

        return i >= 0 ? s.substring(0, i) : s.substring(0, s.length);
    }

    return s;
};

const createSortFnByField = <T, >(
    fieldName: keyof T,
    options: Intl.CollatorOptions = { numeric: true, sensitivity: 'base' },
    locales?: string | string[],
): (a: T, b: T) => number =>
        (a: T, b: T) => {
            const first = a[fieldName] ?? '';
            const second = b[fieldName] ?? '';

            if (typeof first === 'number' && typeof second === 'number') {
                return first - second;
            }

            return `${first}`.localeCompare(`${second}`, locales, options);
        };

/**
 * Capitalizes first character of words in a string.
 *
 * @param str String to be modified
 * @param firstOnly Whether should only the first word begin with a capital letter
 *
 * @example capitalize('Job Name', true) -> "Job name"
 * @example capitalize('Job name') -> "Job Name"
 */
const capitalize = (str: string, firstOnly = false): string => str.split(' ')
    .map((w, i) =>
        ((((firstOnly && i === 0) || !firstOnly) ? w.charAt(0).toUpperCase() : w.charAt(0).toLowerCase()) + w.slice(1)))
    .join(' ');

const formatStr = (str: string, ...args: any[]): string => str.replace(/{\d+?}/g, (substr: string) => args[+substr.slice(1, -1)]);

const plural = (value: number, singular: string, plural?: string, withValue = true) => (
    `${withValue ? value : ''} ${value === 1 ? singular : (plural ? plural : `${singular}s`)}`.trim()
);

export {
    generateGuid,
    generateRandomValue,
    htmlEncode,
    htmlDecode,
    isEncode,
    getStrBetween,
    createSortFnByField,
    capitalize,
    formatStr,
    plural,
};
